import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MoonMark from "@/components/MoonMark";
import { Reveal } from "@/components/Reveal";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  pending: { label: "Awaiting reply from the vault", cls: "border-line text-slate-400" },
  in_progress: { label: "In the studio", cls: "border-gold/60 text-gold" },
  fulfilled: { label: "Fulfilled", cls: "border-emerald-500/60 text-emerald-400" },
};

const Account = () => {
  const [user, setUser] = useState(null);
  const [state, setState] = useState("checking");
  const [quotes, setQuotes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busyId, setBusyId] = useState("");

  const loadQuotes = async () => {
    try {
      const res = await fetch(`${API}/api/quotes/mine`, { credentials: "include" });
      if (!res.ok) throw new Error();
      setQuotes(await res.json());
    } catch {
      setQuotes([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const u = await res.json();
        setUser(u);
        setState("in");
        await loadQuotes();
      } catch {
        setState("out");
      }
    };
    init();
  }, []);

  const respond = async (id) => {
    const text = (drafts[id] || "").trim();
    if (!text) return;
    setBusyId(id);
    try {
      await fetch(`${API}/api/quotes/${id}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });
      setDrafts({ ...drafts, [id]: "" });
      await loadQuotes();
    } finally {
      setBusyId("");
    }
  };

  if (state === "checking") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ink">
        <MoonMark className="h-12 w-12 animate-spin text-gold" style={{ animationDuration: "6s" }} />
        <p className="font-jmono text-[10px] uppercase tracking-[0.4em] text-slate-500">Consulting the ledger…</p>
      </main>
    );
  }

  if (state === "out") {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-8 px-6 pb-24 pt-28 text-center">
        <MoonMark className="h-12 w-12 text-gold" />
        <h1 className="font-display text-3xl tracking-[0.15em] text-silver">THE LEDGER IS SEALED</h1>
        <p className="max-w-md text-sm font-light text-slate-400">
          Sign in with your email and password to see your quotes, their standing, and the
          conversation with the vault.
        </p>
        <Link to="/auth" data-testid="account-goto-auth" className="btn-night">
          Sign In or Join
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-6 pb-24 pt-32 lg:px-10" data-testid="account-page">
      <Reveal>
        <p className="eyebrow">My Quotes</p>
        <h1 className="mt-6 font-display text-3xl tracking-[0.12em] text-silver md:text-4xl">
          Welcome back, {user?.name || "friend of the moon"}
        </h1>
        <p className="mt-4 text-sm font-light text-slate-400">
          Every estimate you have sent, with its standing and the ongoing conversation.
        </p>
      </Reveal>

      <div className="mt-12 space-y-8">
        {quotes.length === 0 && (
          <div className="border border-line bg-surface p-10 text-center" data-testid="account-empty">
            <p className="font-cormorant text-xl italic text-slate-500">
              No quotes yet — the calculator awaits your first commission.
            </p>
            <Link to="/services" data-testid="account-empty-cta" className="btn-night mt-8">
              Shape an Estimate
            </Link>
          </div>
        )}

        {quotes.map((q, i) => {
          const st = STATUS[q.status || "pending"] || STATUS.pending;
          return (
            <Reveal key={q.id} delay={0.05 * i}>
              <article className="border border-line bg-surface p-6" data-testid={`quote-card-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h2 className="font-display text-lg tracking-[0.08em] text-silver">
                    {q.quote?.service || "Custom request"}
                  </h2>
                  <span
                    className={`border px-3 py-1 font-jmono text-[9px] uppercase tracking-[0.25em] ${st.cls}`}
                    data-testid={`quote-status-${i}`}
                  >
                    {st.label}
                  </span>
                  {q.receipt?.paid && (
                    <span className="border border-emerald-500/60 px-3 py-1 font-jmono text-[9px] uppercase tracking-[0.25em] text-emerald-400" data-testid={`quote-paid-chip-${i}`}>
                      Paid
                    </span>
                  )}
                  {q.receipt && (
                    <Link
                      to={`/receipt/${q.id}`}
                      data-testid={`quote-receipt-link-${i}`}
                      className="border border-gold/50 px-3 py-1 font-jmono text-[9px] uppercase tracking-[0.25em] text-gold transition-all duration-300 hover:bg-gold/10"
                    >
                      Print Receipt
                    </Link>
                  )}
                </div>
                {q.quote?.estimate && (
                  <p className="mt-2 font-jmono text-[11px] tracking-[0.2em] text-gold/80">
                    {`$${q.quote.estimate.toLocaleString()} USD`} · {q.quote.scale || ""} · {q.quote.licensing || ""}
                  </p>
                )}
                <p className="mt-4 whitespace-pre-wrap text-sm font-light leading-relaxed text-slate-300">
                  {q.message}
                </p>
                <p className="mt-3 font-jmono text-[10px] tracking-[0.2em] text-slate-600">
                  {q.created_at ? new Date(q.created_at).toLocaleString() : ""}
                </p>

                {(q.replies || []).length > 0 && (
                  <div className="mt-6 space-y-4 border-l-2 border-gold/40 pl-4" data-testid={`quote-thread-${i}`}>
                    {q.replies.map((r, j) => (
                      <div key={j}>
                        <p className="font-jmono text-[9px] uppercase tracking-[0.3em] text-gold/80">
                          {r.from === "nox" ? "Nox Harmonia" : r.name || "You"} ·{" "}
                          <span className="text-slate-600">{r.at ? new Date(r.at).toLocaleString() : ""}</span>
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-sm font-light leading-relaxed text-slate-300">
                          {r.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6">
                  <textarea
                    className="input-night min-h-[80px] resize-y"
                    placeholder="Respond to the vault…"
                    value={drafts[q.id] || ""}
                    onChange={(e) => setDrafts({ ...drafts, [q.id]: e.target.value })}
                    data-testid={`quote-response-textarea-${i}`}
                  />
                  <button
                    className="btn-ghost-night mt-4 !px-5 !py-2"
                    onClick={() => respond(q.id)}
                    disabled={busyId === q.id}
                    data-testid={`quote-respond-btn-${i}`}
                  >
                    {busyId === q.id ? "Sending…" : "Send Response"}
                  </button>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>
    </main>
  );
};

export default Account;
